"use client";

import { useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { PASTILLE_ACTION } from "./ToolsDossier";
import { useCollecteExplications, useNombreExplications } from "./Explanation";
import { useSourcesVerifiees } from "./SourcesVerifiees";
import { documentNoteDeCalcul } from "@/lib/tools/calculationNote";

/**
 * « Télécharger la note de calcul » : les montants que l'outil affiche en ce
 * moment, avec leur calcul et leurs sources, dans un PDF daté.
 *
 * LA NOTE SUIT L'ÉCRAN. Elle relit, au moment du clic, les montants expliqués
 * que l'outil a inscrits (components/Tools/Explanation.jsx) : ce que la
 * personne vient de régler est exactement ce qu'elle emporte.
 *
 * Tout se fait dans le navigateur : ses chiffres ne quittent pas son
 * ordinateur, et jspdf n'est chargé qu'au clic.
 */

const TEXTES = {
    fr: {
        bouton: "Télécharger la note de calcul",
        preparation: "Préparation du PDF…",
        aide: "Le PDF daté reprend chaque montant affiché, avec son calcul pas à pas et ses sources officielles. Vous pouvez le garder ou le donner à votre comptable.",
        vide: "Remplissez d'abord l'outil : la note reprend les montants qu'il affiche.",
        erreur: "Le PDF n'a pas pu être fabriqué. Réessayez dans un instant.",
        fichier: "note-de-calcul",
    },
    en: {
        bouton: "Download the calculation note",
        preparation: "Preparing the PDF…",
        aide: "The dated PDF lists every amount shown, with its step-by-step calculation and its official sources. You can keep it or hand it to your accountant.",
        vide: "Fill in the tool first: the note covers the amounts it shows.",
        erreur: "The PDF could not be produced. Please try again in a moment.",
        fichier: "calculation-note",
    },
};

export default function ToolsCalculationNote({ lang, nomOutil, slug, url, avertissement }) {
    const T = TEXTES[lang] || TEXTES.fr;
    const registre = useCollecteExplications();
    const { sources: catalogue, dates } = useSourcesVerifiees();
    const [message, setMessage] = useState(null);
    const [enCours, setEnCours] = useState(false);
    // Rien à noter tant qu'aucun montant explicable n'est affiché : le bouton
    // n'apparaît qu'avec le premier, plutôt que de répondre « remplissez
    // d'abord l'outil » à qui clique.
    const nombre = useNombreExplications();

    const telecharger = async () => {
        setMessage(null);
        const explications = registre ? [...registre.current.values()] : [];
        const dateIso = new Date().toISOString().slice(0, 10);
        const doc = documentNoteDeCalcul({ lang, nomOutil, url, dateIso, explications, catalogue, dates, avertissement });
        if (!doc.parties) {
            setMessage(T.vide);
            return;
        }
        setEnCours(true);
        try {
            const { composerPdf } = await import("@/lib/tools/documentPdf");
            const pdf = await composerPdf({ document: doc });
            pdf.save(`${T.fichier}-${slug}-${dateIso}.pdf`);
        } catch {
            setMessage(T.erreur);
        } finally {
            setEnCours(false);
        }
    };

    if (nombre === 0) return null;

    // Une pastille sur la ligne des actions du résultat (ToolsDossier) ; ce
    // que contient le PDF se lit au survol, le message ne parle qu'en cas
    // d'échec.
    return (
        <>
            <button
                type="button"
                onClick={telecharger}
                disabled={enCours}
                aria-busy={enCours}
                title={T.aide}
                className={PASTILLE_ACTION}
            >
                <SolarIcon name="document-text-linear" className="h-4 w-4 text-gray-500 dark:text-lightBlue/70" />
                {enCours ? T.preparation : T.bouton}
            </button>
            <span role="status" className="text-xs text-red-700 dark:text-red-400">
                {message}
            </span>
        </>
    );
}
